import { useState, useEffect } from 'react';

export interface SywCategory {
  id: string;
  name: string;
  description?: string;
  iconUrl?: string;
  sortOrder: number;
  active: boolean;
}

export interface SywComponent {
  sku: string;
  name: string;
  description?: string;
  priceEur: number;
  installMinutes: number;
  type: string;
  categoryId?: string;
  imageUrl?: string;
  active: boolean;
  // Shopify-sourced rows only (absent in SYW fallback):
  variantId?: string; // Storefront variant GID
  productId?: string; // Storefront product GID — groups colour variants
  colorName?: string; // "Cor"/"Color" option value — drives bulk colour apply
}

export async function apiFetch<T>(path: string): Promise<T> {
  const res = await fetch(path);
  if (!res.ok) throw new Error(`API ${res.status}: ${path}`);
  const json = (await res.json()) as { success: boolean; data: T };
  return json.data;
}

export function useCategories() {
  const [data, setData] = useState<SywCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<SywCategory[]>('/api/syhouse/categories')
      .then(setData)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  return { data, loading, error };
}

// Catalog rows for one category (Shopify w/ SYW fallback) — used by ColorPage
// to load every opted-in division's catalog without mounting extra hooks.
export function fetchCatalogByCategory(categoryId: string): Promise<SywComponent[]> {
  return apiFetch<SywComponent[]>(`/api/catalog?category=${encodeURIComponent(categoryId)}`);
}

export function useComponents(categoryId?: string) {
  const [data, setData] = useState<SywComponent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Phase 3: per-category catalog comes from Shopify Storefront via the
    // backend /api/catalog route (real product images/prices). No category →
    // full SYW components list (install/pricing reference data).
    const path = categoryId
      ? `/api/catalog?category=${encodeURIComponent(categoryId)}`
      : '/api/syhouse/components';
    apiFetch<SywComponent[]>(path)
      .then(setData)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [categoryId]);

  return { data, loading, error };
}

// Server-side catalog search by name OR SKU (Shopify, via /api/catalog/search).
// Debounced; independent of the SYW KV worker and the Storefront token.
export function useCatalogSearch(query: string) {
  const [results, setResults] = useState<SywComponent[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      apiFetch<SywComponent[]>(`/api/catalog/search?q=${encodeURIComponent(q)}`)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  return { results, loading };
}

export function useWizardTemplate(wizardType: string | null) {
  const [divisions, setDivisions] = useState<Array<{
    id: string; kind: string; label: string; optedIn: boolean;
  }>>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!wizardType) return;
    setLoading(true);
    apiFetch<{ defaultDivisions: typeof divisions }>(`/api/wizard/template/${encodeURIComponent(wizardType)}`)
      .then((t) => setDivisions(t.defaultDivisions))
      .catch(() => setDivisions([]))
      .finally(() => setLoading(false));
  }, [wizardType]);

  return { divisions, loading };
}
